/** * スケジュール編集コンポーネント */
<template>
  <div class="schedule-edit-form">
    <!-- フォームグループ(本体) -->
    <form-group
      id="schedule-form"
      ref="form"
      v-bind:onIfieldApiError="handleApiError"
      v-on:form-group-value-change="$_onFormValueChange"
    />
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { FUNCTION_TYPE } from '@/constants.js'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { mapMutations } from 'vuex'
import { mapGetters } from 'vuex'
import moment from 'moment'
import Schedule from '@/model/Schedule.js'
import SchedulePeriodFormatter from '@/js/SchedulePeriodFormatter.js'
import { toLocalTz, toLocalTzAttribute, toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import _ from 'lodash'

// フォーム用日時フォーマット
const DATETIME_FORMAT_FORM = 'YYYY-MM-DD HH:mm:ss'

// API用日時フォーマット
const DATETIME_FORMAT_API = 'YYYY-MM-DDTHH:mm:ss'

// メッセージ送信日時用のフォーマット
const DATETIME_FORMAT_MILLISECOND = 'YYYY-MM-DDTHH:mm:ss.SSS'

/**
 * 未選択時の報告書プロジェクトID
 * @type {number}
 */
const UNSELECTED_REPORT_PROJECTS_ID = -1

/**
 * 項目名一覧
 */
const FIELD_NAME = {
  // 日時
  PERIOD: 'period',
  // タイトル
  TITLE: 'title',
  // コメント
  DETAIL: 'detail',
  // プレイス
  PLACE: 'place',
  // 報告書プロジェクト
  REPORT_PROJECT: 'report_project',
  // 担当者
  TARGET_USERS: 'target_users',
  // 添付ファイル
  ATTACH_FILES: 'attach_files',
}

// ユーザー属性用複数項目セパレータ
const ATTRIBUTE_VALUES_SEPARATOR = '|'

/**
 * 時間の差分を取得する(end - start)
 * 不正な場合はnull
 * @param {String|Date} start 開始
 * @param {String|Date} end 終了
 * @returns {Number} 差分
 */
const getTimeDiff = (start, end) => {
  const startMoment = moment(start)
  const endMoment = moment(end)
  if (startMoment.isValid() && endMoment.isValid()) {
    return endMoment - startMoment
  } else {
    return null
  }
}

/**
 * 対象ユーザー/組織をフォーム用の値に変換
 * @param {Schedule|ScheduleDefaultValues} source データソース
 * @returns {Object|null} 値
 */
const toTargetValue = (source) => {
  const orgainzationIds = (source.targetOrganizations || []).map(
    (o) => o.organization_id
  )
  const userIds = (source.targetUsers || []).map((u) => u.user_id)
  if (orgainzationIds.length + userIds.length === 0) {
    return null
  } else {
    return {
      organization: orgainzationIds,
      user: userIds,
    }
  }
}

/**
 * デフォルト値クラス
 */
class ScheduleDefaultValues {
  /**
   * コンストラクタ
   * @param {Object} values 初期値オブジェクト
   */
  constructor(values) {
    const start = moment().startOf('hour')
    this.period = (values && values.period) || {
      start: start.format(DATETIME_FORMAT_FORM),
      end: start.add(1, 'hours').format(DATETIME_FORMAT_FORM),
      allday: false,
    }
    this.title = (values && values.title) || null
    this.detail = (values && values.detail) || null
    this.place = (values && values.place) || null
    this.reportProject = (values && values.reportProject) || null
    this.targetUsers = (values && values.targetUsers) || []
    this.targetOrganizations = values?.targetOrganizations || []
    this.scheduleAttachFiles = (values && values.scheduleAttachFiles) || []
  }

  /**
   * 開始日時
   * @type {String}
   */
  get start() {
    return (this.period && this.period.start) || null
  }

  /**
   * 終了日時
   * @type {String}
   */
  get end() {
    return (this.period && this.period.end) || null
  }

  /**
   * 終日フラグ
   * @type {Boolean}
   */
  get allday() {
    return this.period && this.period.allday
  }

  /**
   * プレイスID
   * @type {Number}
   */
  get placeId() {
    return (this.place && this.place.id) || null
  }

  /**
   * プレイス選択無効化フラグ
   * @type {Boolean}
   */
  get placeDisabled() {
    return this.place && this.place.disabled
  }

  /**
   * 報告書プロジェクトID
   * @type {Number}
   */
  get reportProjectId() {
    return (this.reportProject && this.reportProject.id) || null
  }

  /**
   * 報告書プロジェクト選択無効化フラグ
   * @type {Boolean}
   */
  get reportProjectDisabled() {
    return this.reportProject && this.reportProject.disabled
  }
}

export default {
  name: 'AppScheduleEditor',
  props: {
    /**
     * ドメイン名称
     */
    domainName: {
      type: String,
      default: function () {
        return this.$t('appScheduleEditor.schedule')
      },
    },
    /**
     * 対象者にメッセージを送信する場合はtrue
     */
    sendMessage: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 対象のスケジュールデータ
       * @type {Schedule}
       */
      schedule: null,
      /**
       * 新規作成フラグ
       * @type {Boolean}
       */
      isCreate: false,
      /**
       * 報告書プロジェクト
       * @type {Array.<Object>}
       */
      reportProjects: [],
      /**
       * フォームインスタンス
       * @type {Component}
       */
      form: null,
      /**
       * フォーム入力値
       * @type {Object}
       */
      formValues: null,
      /**
       * 保持用期間
       * @type {Object}
       */
      period: {
        start: null,
        end: null,
        allday: false,
      },
      /**
       * ロード中フラグ
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * スケジュール属性
       * @type {Array.<Object>}
       */
      attributes: [],
    }
  },
  computed: {
    ...mapGetters('place', {
      placeKeywordTargets: 'keywordTargets',
    }),
    ...mapGetters('userKeywordTarget', {
      userKeywordTargets: 'keywordTargets',
    }),
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * チェックイン機能が利用可能な場合はtrue
     * @type {Boolean}
     */
    isCheckinAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.CHECKIN)
    },
    /**
     * 報告書機能が利用可能な場合はtrue
     * @type {Boolean}
     */
    isReportAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.REPORT)
    },
    /**
     * 項目ラベルMap(key: 項目名, value: 表示ラベル)
     * @return {Map}
     */
    fieldLabelMap: function () {
      return new Map([
        [FIELD_NAME.PERIOD, this.$t('appScheduleEditor.field.dateTime')],
        [FIELD_NAME.TITLE, this.$t('appScheduleEditor.field.title')],
        [FIELD_NAME.DETAIL, this.$t('appScheduleEditor.field.comment')],
        [FIELD_NAME.PLACE, this.$t('appScheduleEditor.field.place')],
        [
          FIELD_NAME.REPORT_PROJECT,
          this.$t('appScheduleEditor.field.reportProject'),
        ],
        [
          FIELD_NAME.TARGET_USERS,
          this.$t('appScheduleEditor.field.targetUsers'),
        ],
        [
          FIELD_NAME.ATTACH_FILES,
          this.$t('appScheduleEditor.field.attachFiles'),
        ],
      ])
    },

    /**
     * スケジュール属性のID文字列配列
     * @type {Array.<String>}
     */
    scheduleAttributeIdStrings: function () {
      if (!this.attributes) {
        return []
      }
      return this.attributes.map((attr) => `${attr.id}`)
    },

    /**
     * フォーム用データ(定型分)
     * @type {Array.<Object>}
     */
    formsDefault: function () {
      const form = [
        // 日時
        {
          fieldName: FIELD_NAME.PERIOD,
          label: this.fieldLabelMap.get(FIELD_NAME.PERIOD),
          attributeType: ATTRIBUTE_TYPE.PERIOD,
          value: this.formValues
            ? this.formValues[FIELD_NAME.PERIOD]
            : { ...this.period },
          validateRule: {
            required: true,
          },
        },
        // タイトル
        {
          fieldName: FIELD_NAME.TITLE,
          label: this.fieldLabelMap.get(FIELD_NAME.TITLE),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.$_getValueForForm(FIELD_NAME.TITLE, 'title'),
          validateRule: {
            required: true,
            max: 128,
          },
          placeholder: this.$t('appScheduleEditor.placeholder.title'),
        },
        // コメント
        {
          fieldName: FIELD_NAME.DETAIL,
          label: this.fieldLabelMap.get(FIELD_NAME.DETAIL),
          attributeType: ATTRIBUTE_TYPE.TEXT,
          value: this.$_getValueForForm(FIELD_NAME.DETAIL, 'detail'),
          placeholder: this.$t('appScheduleEditor.placeholder.comment'),
        },
        // プレイス
        this.isCheckinAvailable && {
          fieldName: FIELD_NAME.PLACE,
          label: this.fieldLabelMap.get(FIELD_NAME.PLACE),
          attributeType: ATTRIBUTE_TYPE.SINGLE_PLACE,
          value: this.$_getValueForForm(FIELD_NAME.PLACE, 'placeId'),
          disabled: this.isCreate
            ? this.defaultValues.placeDisabled
            : this.schedule.isCheckinCompleted,
          keywordTarget: this.placeKeywordTargets,
          onKeywordTargetChange: (data) =>
            this.$store.dispatch('place/setKeywordTargets', data),
        },
        // 報告書プロジェクト
        this.isReportAvailable && {
          fieldName: FIELD_NAME.REPORT_PROJECT,
          label: this.fieldLabelMap.get(FIELD_NAME.REPORT_PROJECT),
          attributeType: ATTRIBUTE_TYPE.SINGLE_SELECT,
          value: this.$_getValueForForm(
            FIELD_NAME.REPORT_PROJECT,
            'reportProjectId'
          ),
          selectValues: this.reportProjectsForForm,
          disabled: this.isCreate
            ? this.defaultValues.reportProjectDisabled
            : this.schedule.isReportCompleted,
        },
      ]

      if (this.isManager) {
        // 対象ユーザーフォームを追加
        form.unshift({
          fieldName: FIELD_NAME.TARGET_USERS,
          label: this.fieldLabelMap.get(FIELD_NAME.TARGET_USERS),
          attributeType: ATTRIBUTE_TYPE.MULTI_USER,
          // _targets は実スケジュールデータには存在しない仮想キー
          // (target_users と target_organizations を総称した名称として使用)
          value: this.$_getValueForForm(FIELD_NAME.TARGET_USERS, '_targets'),
          disabled: this.isEndSchedule,
          validateRule: {
            required: true,
          },
          allOrganizations: true,
          primaryOrganizationId:
            this.$store.getters['userProfile/defaultOrganizationId'],
          indoorSites: this.$store.getters['indoorSite/indoorSites'],
          keywordTarget: this.userKeywordTargets,
          onKeywordTargetChange: (data) =>
            this.$store.dispatch('userKeywordTarget/setKeywordTargets', data),
        })
      }

      return form
    },
    /**
     * フォーム用データ(スケジュール属性分)
     * @type {Array.<Object>}
     */
    formsAttribute: function () {
      if (!this.attributes || this.attributes.length === 0) {
        return []
      }
      const sortedAttributes = _.sortBy(this.attributes, [
        (item) => item.sort_order,
      ])
      return sortedAttributes.map((attr) => {
        const field = {
          fieldName: `${attr.id}`,
          label: attr.attribute_name,
          attributeType:
            // 文字列属性の複数行表示対応のため attribute_type を読み替える
            // DBのcode_settingの値変更は影響範囲が広く不可なので、フロント側で読み替えている
            attr.attribute_type == ATTRIBUTE_TYPE.STRING
              ? ATTRIBUTE_TYPE.TEXT
              : +attr.attribute_type,
          value: this.$_getAttributeValueFromData(attr.id),
          validateRule: {
            required: attr.required,
          },
        }

        let selectValues = null
        if (+attr.attribute_type === ATTRIBUTE_TYPE.SINGLE_SELECT) {
          if (attr.select_value) {
            selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
          field.selectValues = [{ value: null, text: '' }].concat(
            selectValues || []
          )
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.CHECK) {
          if (attr.select_value) {
            field.selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.TOGGLE) {
          if (attr.select_value) {
            const labels = attr.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
            field.selectValues = [
              { value: true, text: labels[0] },
              { value: false, text: labels[1] },
            ]
          } else {
            // トグルの場合selectValues未指定はフォームが表示されてしまうので属性値の場合はわざとエラーにする
            field.selectValues = []
          }
        }

        return field
      })
    },
    /**
     * フォーム用データ(添付ファイル分)
     * @type {Array.<Object>}
     */
    formsAttachFiles: function () {
      return [
        {
          fieldName: FIELD_NAME.ATTACH_FILES,
          label: this.fieldLabelMap.get(FIELD_NAME.ATTACH_FILES),
          attributeType: ATTRIBUTE_TYPE.FILE,
          value: this.$_getValueForForm(
            FIELD_NAME.ATTACH_FILES,
            'scheduleAttachFiles'
          ),
          multiple: true,
          accept:
            'image/png,image/jpeg,image/gif,image/tiff,application/pdf,video/mp4',
          acceptLabel: 'png, jpg, gif, tiff, pdf, mp4',
          recordId: this.isCreate ? null : this.schedule.id,
        },
      ]
    },
    /**
     * フォーム用データ(結合)
     * @type {Array.<Object>}
     */
    forms: function () {
      return [
        ...this.formsDefault,
        ...this.formsAttribute,
        ...this.formsAttachFiles,
      ].filter((f) => f)
    },

    /**
     * フォーム選択肢用報告書プロジェクトデータ
     * @type {Array.<Object>}
     */
    reportProjectsForForm: function () {
      const selectValues = this.reportProjects.map((project) => {
        return {
          text: project.report_project_name,
          value: project.id,
        }
      })
      selectValues.unshift({ text: '', value: UNSELECTED_REPORT_PROJECTS_ID })
      return selectValues
    },
    /**
     * スケジュールの終了フラグ
     * @type {Boolean}
     */
    isEndSchedule: function () {
      return this.schedule && this.schedule.endFlg
    },
  },
  watch: {
    userKeywordTargets: function () {
      // FIXME:日時フォームのバリデーション状態取得
      // タイトルは初期値が無いので再バリデーションに任せても問題ない
      let provider = this.form.$_getForm(FIELD_NAME.PERIOD).provider
      const flags = {}
      for (const key of Object.keys(provider)) {
        flags[key] = { ...provider[key].flags }
      }

      // 値を保持したままフォームを再セットする
      this.$_getFormValues()
      this.$_setData()

      // 日時フォームのバリデーション状態復元
      this.$nextTick(() => {
        provider = this.form.$_getForm(FIELD_NAME.PERIOD).provider
        for (const key of Object.keys(provider)) {
          provider[key].flags = flags[key]
        }
      })
    },
  },
  created: function () {
    this.formatter = new SchedulePeriodFormatter({
      allDayText: this.$t('appScheduleEditor.allDay'),
    })
    this.$_updateIndoorSites()
  },
  mounted: function () {
    this.form = this.$refs['form']
    this.$log.info('AppScheduleEditor mounted.')
  },
  methods: {
    ...mapMutations({
      /**
       * 保存したスケジュール内容をstoreに格納する
       */
      $_registerLastSavedSchedule: 'updateScheduleLastSaved',
    }),

    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * フォームを表示する
     *
     * @param {Object} [data=null] 更新対象データ
     * @param {String} [defaultValues=null] 初期値オブジェクト
     * @public
     */
    show: async function (data = null, defaultValues = null) {
      // データセット
      this.schedule = new Schedule(data, this.$log)
      this.defaultValues = new ScheduleDefaultValues(defaultValues)
      this.isCreate = data === null

      try {
        await Promise.all([
          this.isReportAvailable && this.$_getReportProjects(),
          this.$_getScheduleAttributes(),
        ])
      } catch {
        this.$_hideLoading()
        return
      }

      this.$_initializePeriod()
      this.$_setData()
    },

    /**
     * フォームをクリアする
     * @public
     */
    clear: function () {
      this.form.setData([])
      this.formValues = null
    },

    /**
     * 報告書プロジェクトを取得する
     */
    $_getReportProjects: async function () {
      const url = RestApiClient.createApiUrl('report_project')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.reportProjects = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * スケジュール属性を取得する
     */
    $_getScheduleAttributes: async function () {
      const url = RestApiClient.createApiUrl('schedule_attribute')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.attributes = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * データをフォームにセットする
     */
    $_setData: function () {
      const form = this.$refs['form']
      form && form.setData(this.forms)
    },

    /**
     * dataから指定スケジュール属性IDの値を取得する
     * @param {Number} id スケジュール属性ID
     * @returns {*} 値
     */
    $_getAttributeValueFromData: function (id) {
      if (this.isCreate) {
        return null
      }

      const matchedAttribute = this.schedule.attributes.find(
        (attr) => attr.attribute_id === id
      )
      if (!matchedAttribute) {
        return null
      }

      return this.$_convertAttributeValueToForm(matchedAttribute)
    },

    /**
     * スケジュール属性の値をフォーム用に変換する
     * @param {Object} attribute 属性データ
     * @returns {*} 値
     */
    $_convertAttributeValueToForm: function (attribute) {
      const type = attribute.attribute_type
      const value = attribute.attribute_value

      if (value == null) {
        return value
      } else if (type == ATTRIBUTE_TYPE.DATETIME) {
        return moment(value).format(DATETIME_FORMAT_FORM)
      } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === attribute.attribute_id
        )
        const selectValues = baseAttribute.select_value
          ? baseAttribute.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
          : []
        return !selectValues.indexOf(value)
      } else if (value.indexOf(ATTRIBUTE_VALUES_SEPARATOR) > 0) {
        return value.split(ATTRIBUTE_VALUES_SEPARATOR)
      } else {
        return value
      }
    },

    /**
     * 保持用の期間データを初期化する
     */
    $_initializePeriod: function () {
      this.period = {
        start: this.isCreate
          ? this.defaultValues.start
          : this.schedule.startAt.format(DATETIME_FORMAT_FORM),
        end: this.isCreate
          ? this.defaultValues.end
          : this.schedule.endAt.format(DATETIME_FORMAT_FORM),
        allday: this.isCreate
          ? this.defaultValues.allday
          : this.schedule.isAllDay,
      }
    },

    /**
     * フォームに設定する値を取得する
     * @param {String} formKey フォームの対象フィールドキー
     * @param {String} scheduleKey スケジュールの対象フィールドキー
     * @returns {*} 値
     */
    $_getValueForForm: function (formKey, scheduleKey) {
      if (this.formValues) {
        return this.formValues[formKey]
      }

      const source = this.isCreate ? this.defaultValues : this.schedule
      if (scheduleKey === '_targets') {
        return toTargetValue(source)
      }

      const value = source[scheduleKey]
      if (scheduleKey === 'scheduleAttachFiles') {
        return value?.length ? { attachFiles: value } : null
      } else {
        return value
      }
    },

    /**
     * カレントの設定ユーザーを取得する
     * @returns {Array.<Number>} ユーザーID配列
     * @public
     */
    getCurrentTargetUsers: function () {
      if (!this.isManager) {
        // 承認権限が無い場合は設定ユーザーが存在しない
        return []
      }
      return this.form
        .getValues(true)
        .find((item) => item.name === FIELD_NAME.TARGET_USERS).value
    },

    /**
     * カレントの設定ユーザーを設定する
     * @param {Array.<Number>} userIds ユーザーID配列
     * @public
     */
    setCurrentTargetUsers: function (userIds) {
      this.form.setValue(FIELD_NAME.TARGET_USERS, userIds)
    },

    /**
     * フォーム値変更時処理
     * @param {String} fieldName フィールド名
     * @param {*} value 変更値
     */
    $_onFormValueChange: function (fieldName, value) {
      if (fieldName === FIELD_NAME.PERIOD) {
        this.$_onPeriodChange(value)
      }
    },

    /**
     * 期間変更時処理
     * @param {Object} value 変更値
     */
    $_onPeriodChange: function (value) {
      const oldDiff = getTimeDiff(this.period.start, this.period.end)

      // 以下の場合は終了時間をスライドしてフォームに戻す
      // * 開始が入力されている
      // * 終日フラグが変更前・変更後ともにfalsy
      // * 開始の値が変更されている
      // * 変更前の開始終了が両方とも入力されていて正しく差分が取れる
      if (
        value.start &&
        !value.allday &&
        !this.period.allday &&
        value.start !== this.period.start &&
        oldDiff != null
      ) {
        this.period = {
          ...value,
          end: moment(value.start)
            .add(moment.duration(oldDiff))
            .format(DATETIME_FORMAT_FORM),
        }
        this.$log.info('slide end to', this.period.end)
        this.form.setValue(FIELD_NAME.PERIOD, { ...this.period })
      } else {
        this.period = value
      }
    },

    /**
     * 現在のフォーム入力値を取得する
     */
    $_getFormValues: function () {
      const values = this.form.getValues(true)
      this.formValues = Object.fromEntries(values.map((v) => [v.name, v.value]))
    },

    /**
     * スケジュールを保存する
     * @public
     */
    save: async function () {
      // フォームから値を取得
      let values
      try {
        values = this.form.getValues()
      } catch (error) {
        this.$log.info(error)
        return
      }

      if (!this.isCreate) {
        // 担当者バリデーション
        const removedAssignUsers = this.$_getRemovedAssignUsers(values)
        if (removedAssignUsers.length) {
          // アラート
          const res =
            await this.$_showConfirmRemoveAssignUserAlert(removedAssignUsers)
          if (!res) return
        }
      }

      this.$_showLoading()

      // リクエストボディ作成
      const requestBody = this.$_convertValuesToRequestBody(values)

      // API実行
      let json
      try {
        if (this.isCreate) {
          json = await this.$_postSchedule(requestBody)
        } else {
          await this.$_putSchedule(requestBody)
        }
      } catch (error) {
        if (error.status === 409) {
          /**
           * スケジュール保存失敗イベント
           * @param {Number} id 対象スケジュールID
           */
          this.$emit(
            'schedule-editfailed',
            this.isCreate ? null : this.schedule.id
          )
        }
        this.$_hideLoading()
        return
      }

      // 対象のスケジュールを取得
      const savedSchedule = await this.$_getSchedule(
        this.isCreate ? json.data.id : this.schedule.id
      )

      if (savedSchedule) {
        // 保存したスケジュールを取得してstoreに保存
        this.$_registerLastSavedSchedule(savedSchedule)
        // メッセージ送信
        if (this.sendMessage) {
          await this.$_sendScheduleMessage(savedSchedule)
        }
      } else {
        // メッセージの送信は出来ないのでメッセージ送信失敗アラートを表示する
        if (this.sendMessage) {
          this.$_showFailedToSendMessageAlert()
        }
      }

      this.$_showSavedToast()
      /**
       * スケジュール保存完了後のイベント
       * @param {Boolean} isCreate 新規登録かどうか
       */
      this.$emit('schedule-editend', this.isCreate)
      this.$_hideLoading()
    },

    /**
     * 除かれた(選択されていない)担当ユーザーを取得する
     * @param {Object[]} values フォーム入力値
     * @returns {Object[]} 除かれた担当者
     */
    $_getRemovedAssignUsers: function (values) {
      const target = values.find((v) => v.name === FIELD_NAME.TARGET_USERS)
      const { user: userIds } = target.value
      return this.schedule.assignedUsers.filter(
        (u) => !userIds.includes(u.user_id)
      )
    },

    /**
     * 担当ユーザーの削除を確認するアラートを表示する
     * @param {Object[]} users 対象のユーザー
     * @returns {Promise} ユーザーの回答(sweetalert2の仕様に従う)
     */
    $_showConfirmRemoveAssignUserAlert: function (users) {
      const html = `
        <p class="text">
          ${this.$t('appScheduleEditor.message.assignUsersNotSelected')}
        </p>
        <p class="text">
          ${this.$t('appScheduleEditor.message.willNotBeAssigned')}
        </p>
        <p class="text users">
          ${users.map((u) => u.user_name).join(', ')}
        </p>
        <p class="text">
          ${this.$t('appScheduleEditor.message.selectUsersIndividually')}
        </p>
        <p class="text">
          ${this.$t('appScheduleEditor.message.continueSaving')}
        </p>
      `

      return this.$alert
        .fire({
          target: document.body,
          icon: 'warning',
          title: this.$t('appScheduleEditor.confirm'),
          html,
          showCancelButton: true,
          confirmButtonText: this.$t('appScheduleEditor.action.ok'),
          cancelButtonText: this.$t('appScheduleEditor.action.cancel'),
          width: 'auto',
          customClass: {
            htmlContainer: 'confirm-content',
          },
        })
        .then((res) => !!res.value)
    },

    /**
     * リクエストbodyを作成する
     * @param {Array.<Object>} values フォームの値
     * @returns {Object} リクエストボディ
     */
    $_convertValuesToRequestBody: function (values) {
      const valueGroup = _.groupBy(values, (item) =>
        this.scheduleAttributeIdStrings.includes(item.name)
          ? 'attribute'
          : 'default'
      )
      const body = { ...this.schedule.toRequestJSON() }
      // 定型分
      this.$_applyDefaultValuesToRequestBody(body, valueGroup.default)
      // スケジュール属性分
      this.$_applyAttributeValuesToRequestBody(body, valueGroup.attribute)

      return body
    },

    /**
     * 定型分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyDefaultValuesToRequestBody: function (body, values) {
      if (this.isCreate) {
        delete body.complete_flg
      }
      body.tasks = [] // 更新中にpushするので空にしておく
      values.forEach((item) => {
        // タイトル
        if (item.name === FIELD_NAME.TITLE) {
          body.title = item.value
        }
        // コメント
        if (item.name === FIELD_NAME.DETAIL) {
          body.detail = item.value
        }
        // 日時
        if (item.name === FIELD_NAME.PERIOD) {
          const isAllDay = !!item.value.allday
          body.allday_flg = isAllDay
          body.start_at = isAllDay
            ? moment(item.value.start).format(DATETIME_FORMAT_API)
            : toServerTz(item.value.start)
          body.end_at = isAllDay
            ? moment(item.value.end).format(DATETIME_FORMAT_API)
            : toServerTz(item.value.end)
        }
        // 対象ユーザー
        if (item.name === FIELD_NAME.TARGET_USERS) {
          const { user, organization } = item.value

          // 組織は担当者引き継ぎがないので単純に変換
          body.target_organizations = organization.map((id) => ({
            organization_id: id,
          }))

          if (this.isCreate) {
            body.target_users = user.map((id) => ({ user_id: id }))
          } else {
            body.target_users = user.map((id) => {
              // 担当者を引き継ぐため、継続しているユーザーは元の対象ユーザーレコードを使用する
              const originalUser = this.schedule.targetUsers.find(
                (user) => user.user_id === id
              )
              return originalUser || { user_id: id }
            })
          }
        }
        // プレイス
        if (item.name === FIELD_NAME.PLACE) {
          // 値が空の場合はタスクを作成しない
          if (!item.value) return
          const checkinTask = this.$_createNewCheckinTask(item.value)
          body.tasks.push(checkinTask)
        }
        // 報告書プロジェクト
        if (item.name === FIELD_NAME.REPORT_PROJECT) {
          // 値が空の場合はタスクを作成しない
          if (!item.value || item.value === UNSELECTED_REPORT_PROJECTS_ID) {
            return
          }
          const reportTask = this.$_createNewReportTask(item.value)
          body.tasks.push(reportTask)
        }
        // 添付ファイル
        if (item.name === FIELD_NAME.ATTACH_FILES) {
          const { attachFiles, tempFiles } = item.value || {}
          body.schedule_attach_files =
            attachFiles &&
            attachFiles.map((file) => ({
              id: file.id,
            }))
          body.temp_attach_files =
            tempFiles &&
            tempFiles.map((file) => ({
              temp_file_path: file.tempFilePath,
            }))
        }
      })

      // 管理者でない場合は設定ユーザーはnullにする
      if (!this.isManager) {
        body.target_users = null
      }
    },

    /**
     * スケジュール属性分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyAttributeValuesToRequestBody: function (body, values) {
      if (!Array.isArray(values) || values.length === 0) {
        return
      }

      const attributes = values.map((item) => {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === +item.name
        )
        const type = baseAttribute.attribute_type

        // トグル属性がnullのときfalseを代入
        if (type == ATTRIBUTE_TYPE.TOGGLE && item.value == null) {
          item.value = false
        }

        let value
        if (item.value === null) {
          value = item.value
        } else if (type == ATTRIBUTE_TYPE.DATETIME) {
          value = toServerTz(item.value)
        } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
          const selectValues = baseAttribute.select_value.split(
            ATTRIBUTE_VALUES_SEPARATOR
          )
          value = selectValues[+!item.value]
        } else if (Array.isArray(item.value)) {
          value = item.value.join(ATTRIBUTE_VALUES_SEPARATOR)
        } else {
          value = item.value
        }

        return {
          attribute_id: +item.name,
          attribute_value: value,
        }
      })

      body.attributes = attributes
    },

    /**
     * 更新用チェックインタスクを作成する
     *
     * @param {Number} placeId 更新用プレイスID
     * @returns {Object} チェックインタスク
     */
    $_createNewCheckinTask: function (placeId) {
      const originalTask = this.schedule.checkinTask
      let newTask
      if (!originalTask) {
        // 元タスクが無い場合は新規作成
        newTask = {
          function_type: FUNCTION_TYPE.CHECKIN,
          parent_id: placeId,
          id: null,
          remarks: null,
          complete_flg: false,
        }
      } else {
        // 元タスクがある場合はプレイスIDを更新
        newTask = { ...originalTask }
        newTask.parent_id = placeId
        if (originalTask.parent_id !== newTask.parent_id) {
          // プレイスIDが変更された場合は備考をクリアする
          newTask.remarks = null
        }
      }

      return newTask
    },

    /**
     * 更新用報告書タスクを作成する
     *
     * @param {Number} reportProjectId 更新用報告書プロジェクトID
     * @returns {Object} 報告書タスク
     */
    $_createNewReportTask: function (reportProjectId) {
      const originalTask = this.schedule.reportTask
      let newTask
      if (!originalTask) {
        // 元タスクが無い場合は新規作成
        newTask = {
          function_type: FUNCTION_TYPE.REPORT,
          parent_id: reportProjectId,
          id: null,
          remarks: null,
          complete_flg: false,
        }
      } else {
        // 元タスクがある場合はプレイスIDを更新
        newTask = { ...originalTask }
        newTask.parent_id = reportProjectId
        if (originalTask.parent_id !== newTask.parent_id) {
          // プロジェクトIDが変更された場合備考をクリアする
          newTask.remarks = null
        }
      }

      return newTask
    },

    /**
     * スケジュールをPOSTする
     *
     * @param {Object} requestBody リクエストボディ
     */
    $_postSchedule: async function (requestBody) {
      const url = RestApiClient.createApiUrl('schedule')

      try {
        return await RestApiClient.executePostApi(url, requestBody)
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * スケジュールをPUTする
     *
     * @param {Object} requestBody リクエストボディ
     */
    $_putSchedule: async function (requestBody) {
      const url = RestApiClient.createApiUrl(`schedule/${this.schedule.id}`)

      try {
        return await RestApiClient.executePutApi(url, requestBody)
      } catch (error) {
        this.$_hideLoading()
        await this.handleApiError(error)
        throw error
      }
    },

    /**
     * スケジュール登録メッセージを送信する
     * @param {Object} data 登録したスケジュールデータ
     */
    $_sendScheduleMessage: async function (data) {
      const schedule = new Schedule(data)
      const url = RestApiClient.createApiUrl('send_message')
      const body = {
        send_at: toServerTz(+moment(), DATETIME_FORMAT_MILLISECOND),
        schedule_id: schedule.id,
        content: this.$_createMessageContent(schedule),
        receive_users: schedule.targetUsers.map((u) => ({ id: u.user_id })),
        receive_organizations: schedule.targetOrganizations.map((o) => ({
          id: o.organization_id,
        })),
      }
      try {
        await RestApiClient.executePostApi(url, body)
      } catch (error) {
        await this.handleApiError(error, this.$_showFailedToSendMessageAlert)
      }
    },

    /**
     * 単一のスケジュールを取得する
     *
     * @param {Number} id スケジュールID
     * @returns {Promise.<Object>}
     */
    $_getSchedule: async function (id) {
      const url = RestApiClient.createApiUrl(`schedule/${+id}`)

      let d
      try {
        d = await RestApiClient.executeGetApi(url).then(({ data }) => data)
      } catch (error) {
        this.handleApiError(error, null)
        return
      }

      return {
        ...d,
        start_at: d.allday_flg ? d.start_at : toLocalTz(d.start_at),
        end_at: d.allday_flg ? d.end_at : toLocalTz(d.end_at),
        create_at: toLocalTz(d.create_at),
        update_at: toLocalTz(d.update_at),
        attributes: d.attributes.map((attr) => toLocalTzAttribute(attr)),
      }
    },

    /**
     * 送信メッセージ本文を作成する
     *
     * @param {Schedule} schedule 対象スケジュール
     * @returns {String} メッセージ本文
     */
    $_createMessageContent: function (schedule) {
      const lines = []
      // 前置き
      lines.push(`${this.$t('appScheduleEditor.message.newTaskSet')}\n`)
      // タイトル
      lines.push(
        `[${this.fieldLabelMap.get(FIELD_NAME.TITLE)}] ${schedule.title}`
      )
      // 日時
      const period = this.formatter.format(schedule)
      lines.push(`[${this.fieldLabelMap.get(FIELD_NAME.PERIOD)}] ${period}`)
      // 本文
      if (schedule.detail == null) {
        lines.push(`[${this.fieldLabelMap.get(FIELD_NAME.DETAIL)}]`)
      } else {
        lines.push(
          `[${this.fieldLabelMap.get(FIELD_NAME.DETAIL)}] ${schedule.detail}`
        )
      }
      // プレイス(指定時のみ)
      if (schedule.checkinTask) {
        lines.push(
          `[${this.fieldLabelMap.get(FIELD_NAME.PLACE)}] ${schedule.placeName}`
        )
      }
      // 報告書プロジェクト(指定時のみ)
      if (schedule.reportTask) {
        lines.push(
          `[${this.fieldLabelMap.get(FIELD_NAME.REPORT_PROJECT)}] ${
            schedule.reportProjectName
          }`
        )
      }
      // 設定ユーザー
      const userNames = schedule.targetUsers.map((user) => user.user_name)
      const organizationNames = schedule.targetOrganizations.map(
        (d) =>
          `${d.organization_name} (${d.organization_user_relations.length})`
      )
      lines.push(
        `[${this.fieldLabelMap.get(FIELD_NAME.TARGET_USERS)}] ${[
          ...organizationNames,
          ...userNames,
        ].join(', ')}`
      )
      // スケジュール添付ファイル名
      if (schedule.scheduleAttachFiles.length) {
        const fileNames = schedule.scheduleAttachFiles
          .map((file) => file.attach_file_path.split('/').pop())
          .join(', ')
        lines.push(
          `[${this.fieldLabelMap.get(FIELD_NAME.ATTACH_FILES)}] ${fileNames}`
        )
      }

      return lines.join('\n')
    },

    /**
     * メッセージ送信失敗アラートを表示する
     * @param {Error|Response} エラー
     */
    $_showFailedToSendMessageAlert: async function (error) {
      let title = this.$t('appScheduleEditor.message.failedToSendMessage')
      if (error instanceof Response && error.status === 400) {
        try {
          const json = await error.clone().json()
          // errors が空の場合はリクエストの修正ができないのでサーバから返却されたメッセージを表示
          // (firebase の通知キーが古い等の場合を想定)
          if (json.errors == null) {
            title = json.message
          }
        } catch (error) {
          // JSONの取り出しに失敗した場合
          // ログだけ出しておいてユーザーに表示するエラーメッセージはデフォルトのものを使用する
          this.$log.info('failed to parse to json', error)
        }
      }

      return this.$alert.fire({
        icon: 'error',
        title,
        text: this.$t('appScheduleEditor.message.notifyUsersOfTheTask'),
      })
    },

    /**
     * 保存完了トーストを表示する
     */
    $_showSavedToast: function () {
      const message = this.isCreate
        ? this.$t('appScheduleEditor.message.created', {
            domain: this.domainName,
          })
        : this.$t('appScheduleEditor.message.updated', {
            domain: this.domainName,
          })
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: message,
        confirmButtonText: this.$t('appScheduleEditor.action.ok'),
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .confirm-content {
  color: $text-color;
  font-size: $font-size-large;

  .text {
    margin: 0;
    line-height: 1.7;

    &.users {
      max-width: 32rem;
      font-weight: $font-weight-bold;
      margin: 1rem auto;
    }
  }
}
</style>
